import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OnlineAccessSettings } from '@models/onlineAccessSettings';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OnlineAccessService } from '@services/onlineAccess';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  FormGroupComponent,
  FormsUtil,
  InputButton,
  InputComponent,
  MbsPopupType,
  MbsSize,
  MbsValidators,
  SidepanelComponent,
  SidepanelCrudBase,
  SidepanelService,
  SwitcherComponent,
  ToastService
} from 'mbs-ui-kit';
import { SidepanelInfoEvent } from 'mbs-ui-kit/sidepanel/sidepanel.service';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, finalize, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-sidepanel-online-access',
  templateUrl: './sidepanel-online-access.component.html',
  styleUrls: ['./sidepanel-online-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    SidepanelComponent,
    SwitcherComponent,
    CanPipe,
    HelpLinkDirective
  ]
})
export class SidepanelOnlineAccessComponent extends SidepanelCrudBase<any> {
  private ability = inject(AbilityService);
  private i18next = inject(I18NextPipe);
  private sidepanelService = inject(SidepanelService);
  private cdr = inject(ChangeDetectorRef);
  private service = inject(OnlineAccessService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private clipboard = inject(Clipboard);
  private config = inject(ConfigurationService);
  private formBuilder = inject(FormBuilder);

  public readonly sidePanelId = 'online-access-panel';
  public readonly mbsSize = MbsSize;
  public readonly alertType = MbsPopupType;

  public form = this.formBuilder.group({
    enableOnlineAccess: [false],
    optionsForm: this.formBuilder.group({
      enableBackup: false,
      enableConnect: false,
      enableTickets: false,
      alias: ['', [Validators.required, MbsValidators.aliasTextValidator.bind(this)]],
      commonURL: ['']
    })
  });

  public get enableOnlineAccess(): FormControl<boolean> {
    return this.form.get('enableOnlineAccess') as FormControl;
  }

  public get optionsForm(): FormGroup {
    return this.form.get('optionsForm') as FormGroup;
  }

  public get enableBackup(): FormControl<boolean> {
    return this.optionsForm.get('enableBackup') as FormControl;
  }

  public get enableConnect(): FormControl<boolean> {
    return this.optionsForm.get('enableConnect') as FormControl;
  }

  public get enableTickets(): FormControl<boolean> {
    return this.optionsForm.get('enableTickets') as FormControl;
  }

  public get alias(): FormControl<string> {
    return this.optionsForm.get('alias') as FormControl;
  }

  public get commonURL(): FormControl<string> {
    return this.optionsForm.get('commonURL') as FormControl;
  }

  public get isConnectAvailable(): boolean {
    return this.ability.can('read', 'ConnectInOnlineAccess2');
  }

  public get betaLabel(): string {
    return this.i18next.transform('shared:beta').toUpperCase();
  }

  private baseAppendButtons: InputButton[] = [
    {
      text: '',
      id: 'pasteURL',
      type: 'secondary',
      icon: 'fa fa-level-up',
      loading: false
    },
    {
      text: '',
      id: 'copyURL',
      type: 'secondary',
      icon: 'fa fa-files-o',
      loading: false
    }
  ];

  public appendButtonsCommon = this.baseAppendButtons.map((button) => ({ ...button, id: `${button.id}_common` }));

  private commonURLPrefix = this.config.get('onlineAccessHref') ?? '';

  constructor() {
    super();
    this.sidepanelService.onOpen
      .pipe(
        debounceTime(100),
        filter((data: SidepanelInfoEvent) => data?.id === this.sidePanelId && !this.loadingData),
        untilDestroyed(this)
      )
      .subscribe(() => this.updateData());
    this.enableOnlineAccess.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.optionsForm.enable();
      } else {
        this.enableBackup.setValue(false);
        this.isConnectAvailable && this.enableConnect.setValue(false);
        this.enableTickets.setValue(false);
        this.optionsForm.disable();
      }
      this.cdr.markForCheck();
    });
  }

  public updateData(): void {
    this.resetForm();
    this.switchLoading(true);
    this.service
      .getSettings()
      .pipe(finalize(() => this.switchLoading(false)))
      .subscribe((data: OnlineAccessSettings) => {
        this.enableOnlineAccess.setValue(data.enableOnlineAccess);
        this.alias.setValue(data.alias);
        this.alias.markAsDirty();
        this.enableBackup.setValue(data.enableOnlineAccessBackupData);
        this.isConnectAvailable && this.enableConnect.setValue(data.enableOnlineAccessConnect);
        this.enableTickets.setValue(data.enableOnlineAccessTickets);
        this.handleAliasChange();
      });
  }

  switchLoading(state: boolean): void {
    this.loadingData = state;
    this.cdr.markForCheck();
  }

  isValidSidepanel(): boolean {
    return !this.enableOnlineAccess.value || this.form.valid;
  }

  // Save all sidepanel data
  handleSave(): Observable<boolean> {
    if (!this.isValidSidepanel()) {
      FormsUtil.triggerValidation(this.alias);
      return of(false);
    }
    this.cdr.markForCheck();
    // save
    this.service
      .setSettings({
        enableOnlineAccess: this.enableOnlineAccess.value,
        enableOnlineAccessBackupData: this.enableBackup.value,
        enableOnlineAccessConnect: this.isConnectAvailable && this.enableConnect.value,
        enableOnlineAccessTickets: this.enableTickets.value,
        alias: this.alias.value
      })
      .pipe(
        tap(() => {
          this.toastService.success(this.i18next.transform('shared:notifications.successfullySaved'));
          this.genericPanel.close();
        }),
        catchError(() => of(false))
      )
      .subscribe();
    return of(false);
  }

  // Reset sidepanel data
  handleClose(): Observable<boolean> {
    return super.handleClose().pipe(
      tap((result) => {
        if (result) {
          this.resetForm();
        }
      })
    );
  }

  handleDelete(): Observable<boolean> {
    return of(false);
  }

  handleAliasChange(): void {
    const alias = this.alias.value;
    this.commonURL.setValue(`${this.commonURLPrefix}${alias}/`);
    this.cdr.markForCheck();
  }

  resetForm(): void {
    this.form.reset();
  }

  showErrorToast(err: HttpErrorResponse): void {
    const message = typeof err === 'string' ? err : err?.message || err?.error?.message || err?.error?.title || unidentifiedErrorText;
    this.toastService.error(message);
  }

  handleBindingRedirect(): void {
    this.handleClose().subscribe();
    this.router.navigate(['/AP/SSL']);
  }

  handleURLButton(data: InputButton): void {
    const isPasteURLMode = data?.id.startsWith('pasteURL') ?? false;
    const isCopyURLMode = data?.id.startsWith('copyURL') ?? false;
    const isCommonMode = data?.id.endsWith('_common') ?? false;

    if ((!isPasteURLMode && !isCopyURLMode) || !isCommonMode) return;

    const url = this.commonURL.value;

    if (isPasteURLMode) {
      window.open(url, '_blank');
    }
    this.clipboard.copy(url);
  }
}
